import Kendra, { QueryRequest } from "aws-sdk/clients/kendra";
import { AWSError } from "aws-sdk/global";
import { PromiseResult } from "aws-sdk/lib/request";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { ChangeEvent } from "react";
import { Spinner } from "react-bootstrap";
import { QueryResultType, Relevance } from "./constants";
import { getSampleIndexDetails } from "./exampleData/getSampleIndexDetails";
import { getSearchResults } from "./exampleData/getSearchResults";
import { AvailableFacetManager } from "./facets/AvailableFacetManager";
import { SelectedFacetManager } from "./facets/SelectedFacetManager";
import {
  DataSourceNameLookup,
  getAttributeTypeLookup,
  getDataSourceNameLookup,
  IndexFieldNameToDocumentAttributeValueType,
} from "./facets/utils";
import Pagination from "./pagination/Pagination";
import ResultPanel from "./resultsPanel/ResultPanel";
import "./search.scss";
import SearchBar from "./searchBar/SearchBar";
import _ from "lodash";
import { AvailableSortingAttributesManager } from "./sorting/AvailableSortingAttributesManager";
import { SelectedSortingAttributeManager } from "./sorting/SelectedSortingAttributeManager";
import { DEFAULT_SORT_ATTRIBUTE, SortOrderEnum } from "./sorting/constants";
import S3 from "aws-sdk/clients/s3";
import { isNullOrUndefined } from "./utils";
import { Checkbox, Dialog, DialogContent, DialogContentText, FormControl, FormControlLabel, FormGroup, FormLabel, Typography } from "@mui/material";
import AvailableSourceResponse from "../types/availableSourceResponse";
import contentLibraryApi from '../services/contentLibraryApi';
import DocumentMetadataResponse from "../types/documentMetadata";

interface SearchProps {
  /* An authenticated instance of the Kendra SDK */
  kendra?: Kendra;
  /* The ID of an index in the account the Kendra SDK is authenticated for */
  indexId: string;

  s3?: S3;

  facetConfiguration?: {
    facetsToShowWhenUncollapsed: number;
    showCount: boolean;
    updateAvailableFacetsWhenFilterChange: boolean;
    facetPanelDefaultOpen: boolean;
  };
}

interface SearchState {
  dataReady: boolean;
  searchResults: Kendra.QueryResult;
  topResults: Kendra.QueryResultItemList;
  faqResults: Kendra.QueryResultItemList;
  docResults: Kendra.QueryResultItemList;
  currentPageNumber: number;
  queryText: string;
  error?: AWSError;
  index?: Kendra.DescribeIndexResponse;
  facetsOpen: boolean;

  // Faceting state
  attributeTypeLookup?: IndexFieldNameToDocumentAttributeValueType;
  availableFacets: AvailableFacetManager;
  dataSourceNameLookup?: DataSourceNameLookup;
  selectedFacets: SelectedFacetManager;

  // Sorting state
  availableSortingAttributes: AvailableSortingAttributesManager;
  selectedSortingAttribute: SelectedSortingAttributeManager;
  searchFilters: string[],
  dialogResultOpen: boolean;
  dialogResultItem?: Kendra.QueryResultItem;
  dialogResultMetadata?: DocumentMetadataResponse;
  availableSources: AvailableSourceResponse[];
}

export default class Search extends React.Component<SearchProps, SearchState> {
  constructor(props: SearchProps) {
    super(props);

    this.state = {
      dataReady: false,
      searchResults: {},
      topResults: [],
      faqResults: [],
      docResults: [],
      currentPageNumber: 1,
      queryText: "",
      error: undefined,
      attributeTypeLookup: undefined,
      availableFacets: AvailableFacetManager.empty(),
      selectedFacets: SelectedFacetManager.empty(),
      index: undefined,
      facetsOpen:
        (this.props.facetConfiguration &&
          this.props.facetConfiguration.facetPanelDefaultOpen) ??
        false,
      availableSortingAttributes: AvailableSortingAttributesManager.empty(),
      selectedSortingAttribute: SelectedSortingAttributeManager.default(),
      searchFilters: [],
      dialogResultItem: undefined,
      dialogResultOpen: false,
      dialogResultMetadata: undefined,
      availableSources: [],
    };
  }

  async componentDidMount() {
    const { indexId, kendra } = this.props;

    if (kendra) {
      const listDataSourcePromise = this.listDataSources(kendra, indexId);

      const list = await contentLibraryApi.listSources();

      console.log(list);

      this.setState({
        availableSources: list
      });

      try {
        // Create attribute type lookup from index
        const index = await kendra
          .describeIndex({
            Id: indexId,
          })
          .promise();

        this.setState({
          attributeTypeLookup: getAttributeTypeLookup(index),
          index: index,
        });

        // Get available sorting attributes from index meta data
        if (index.DocumentMetadataConfigurations) {
          this.setState({
            availableSortingAttributes: this.state.availableSortingAttributes.fromIndexMetadata(
              index.DocumentMetadataConfigurations
            ),
          });
        }

        // Create data source name lookup
        const dataSources = await listDataSourcePromise;

        this.setState({
          dataSourceNameLookup: getDataSourceNameLookup(dataSources),
        });
      } catch (e) {
      }
    } else {
      // The SDK is not configured, use mock data
      this.setState({
        ...getSampleIndexDetails(),
      });
    }
  }

  listDataSources = async (
    kendra: Kendra,
    indexId: string
  ): Promise<Kendra.DataSourceSummaryList | null> => {
    try {
      let listDsResponse: PromiseResult<
        Kendra.ListDataSourcesResponse,
        AWSError
      > | null = await kendra
        .listDataSources({
          IndexId: indexId,
        })
        .promise();

      const dataSources = listDsResponse.SummaryItems || [];

      while (listDsResponse?.$response.hasNextPage()) {
        const nextPage = listDsResponse.$response.nextPage();
        if (nextPage) {
          listDsResponse = await nextPage.promise();
          if (listDsResponse?.SummaryItems) {
            dataSources.push(...listDsResponse.SummaryItems);
          }
        } else {
          listDsResponse = null;
        }
      }

      return dataSources;
    } catch (e) {
    }

    return null;
  };

  private getResultsHelper = async (
    queryText: string,
    pageNumber: number,
    filter?: Kendra.AttributeFilter
  ) => {
    this.setState({ dataReady: false });

    let results: Kendra.QueryResult = getSearchResults(pageNumber, filter);

    const queryRequest: QueryRequest = {
      IndexId: this.props.indexId,
      QueryText: queryText,
      PageNumber: pageNumber,
      AttributeFilter: filter ? filter : undefined,
    };

    if (this.state.searchFilters.length > 0) {
      queryRequest.AttributeFilter = {
        OrAllFilters: this.state.searchFilters.map(filter => {
          return {
            EqualsTo: {
              Key: '_data_source_id',
              Value: {
                StringValue: filter
              }
            }
          }
        })
      }
    }

    const sortingAttribute = this.state.selectedSortingAttribute.getSelectedSortingAttribute();
    const sortingOrder = this.state.selectedSortingAttribute.getSelectedSortingOrder();

    if (sortingAttribute !== DEFAULT_SORT_ATTRIBUTE) {
      queryRequest.SortingConfiguration = {
        DocumentAttributeKey: sortingAttribute,
        SortOrder: sortingOrder!,
      };
    }

    if (this.props.kendra) {
      try {
        results = await this.props.kendra.query(queryRequest).promise();
      } catch (e) {
        this.setState({
          searchResults: {},
          topResults: [],
          faqResults: [],
          docResults: [],
          dataReady: true
        });
        return;
      }
    } else {
      console.error(
        "WARNING: No Kendra SDK instance provided, using dummy data"
      );
    }

    const tempTopResults: Kendra.QueryResultItemList = [];
    const tempFAQResults: Kendra.QueryResultItemList = [];
    const tempDocumentResults: Kendra.QueryResultItemList = [];

    if (results && results.ResultItems) {
      results.ResultItems.forEach((result: Kendra.QueryResultItem) => {
        if (!isNullOrUndefined(this.props.s3) && result.DocumentURI) {
          try {
            let res = result.DocumentURI.split("/");
            if (res[2].startsWith("s3")) {
              //The URI points to an object in an S3 bucket
              //Get presigned url from s3
              let bucket = res[3];
              let key = res[4];
              for (var i = 5; i < res.length; i++) {
                key = key + "/" + res[i];
              }
              let params = { Bucket: bucket, Key: key };
              let url = this.props.s3!.getSignedUrl("getObject", params);
              result.DocumentURI = url;
            }
          } catch {
            // Just do nothing, so the documentURI are still as before
          }
        }
        switch (result.Type) {
          case QueryResultType.Answer:
            tempTopResults.push(result);
            break;
          case QueryResultType.QuestionAnswer:
            tempFAQResults.push(result);
            break;
          case QueryResultType.Document:
            tempDocumentResults.push(result);
            break;
          default:
            break;
        }
      });

      // Only update availableFacets in two situations:
      // 1. There is no filter
      // 2. There is filter and the updateAvailableFacetsWhenFilterChange flag is true
      if (
        !filter ||
        (filter &&
          this.props.facetConfiguration?.updateAvailableFacetsWhenFilterChange)
      ) {
        this.setState({
          availableFacets: AvailableFacetManager.fromQueryResult(results),
        });
      }

      this.setState({
        searchResults: results,
        topResults: tempTopResults,
        faqResults: tempFAQResults,
        docResults: tempDocumentResults,
        dataReady: true,
        error: undefined,
      });
    } else {
      this.setState({
        searchResults: {},
        topResults: tempTopResults,
        faqResults: tempFAQResults,
        docResults: tempDocumentResults,
        dataReady: true,
        error: undefined,
      });
    }
    this.setState({
      currentPageNumber: pageNumber,
      queryText: queryText,
    });
  };

  // When submitting query from search bar, reset facets and sorting attributes
  getResults = async (queryText: string, pageNumber: number = 1) => {
    this.setState(
      {
        selectedFacets: this.state.selectedFacets.clearAll(),
        selectedSortingAttribute: SelectedSortingAttributeManager.default(),
      },
      () => {
        this.getResultsHelper(queryText, pageNumber);
      }
    );
  };

  getResultsOnPageChanging = async (
    queryText: string,
    pageNumber: number = 1
  ) => {
    this.computeFilterAndReSubmitQuery(queryText, pageNumber);
  };

  submitFeedback = async (
    relevance: Relevance,
    resultItem: Kendra.QueryResultItem
  ) => {
    if (!this.props.kendra) {
      console.error(
        "WARNING: No Kendra SDK instance provided, submit feedback ignored"
      );

      return;
    } else if (!this.props.indexId) {
      console.error(
        "WARNING: No Kendra Index Id provided, submit feedback ignored"
      );

      return;
    }

    const queryResult = this.state.searchResults;
    if (relevance !== Relevance.Click) {
      // Explicit relevance feedback
      const feedbackRequest: Kendra.SubmitFeedbackRequest = {
        IndexId: this.props.indexId,
        QueryId: queryResult.QueryId as string,
        RelevanceFeedbackItems: [
          {
            RelevanceValue: relevance as string,
            ResultId: resultItem.Id as string,
          },
        ],
      };

      this.props.kendra.submitFeedback(feedbackRequest).promise();
    } else {
      // Click feedback
      const feedbackRequest: Kendra.Types.SubmitFeedbackRequest = {
        IndexId: this.props.indexId,
        QueryId: queryResult.QueryId as string,
        ClickFeedbackItems: [
          {
            ClickTime: new Date(),
            ResultId: resultItem.Id as string,
          },
        ],
      };

      this.props.kendra.submitFeedback(feedbackRequest).promise();
    }
  };

  private getErrorNotification = () => {
    return (
      <div className="error-div">
        {!_.isEmpty(this.state.error?.message)
          ? this.state.error?.message
          : this.state.error?.code}
      </div>
    );
  };

  private computeFilterAndReSubmitQuery(
    queryText: string,
    pageNumber: number = 1
  ) {
    const filter = this.state.selectedFacets.buildAttributeFilter(
      this.state.attributeTypeLookup
    );

    this.getResultsHelper(queryText, pageNumber, filter);
  }

  onSelectedFacetsChanged = (updatedSelectedFacets: SelectedFacetManager) => {
    this.setState(
      {
        selectedFacets: updatedSelectedFacets,
      },
      () => {
        this.computeFilterAndReSubmitQuery(this.state.queryText);
      }
    );
  };

  handleClickExpander = () => {
    this.setState({
      ...this.state,
      facetsOpen: !this.state.facetsOpen,
    });
  };

  onSortingAttributeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    this.setState(
      {
        selectedSortingAttribute: this.state.selectedSortingAttribute.setSelectedSortingAttribute(
          event.currentTarget.value
        ),
      },
      () => {
        this.computeFilterAndReSubmitQuery(this.state.queryText);
      }
    );
  };

  onSortingOrderChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const sortingOrder = this.state.selectedSortingAttribute.getSelectedSortingOrder();
    if (sortingOrder === SortOrderEnum.Desc) {
      this.setState(
        {
          selectedSortingAttribute: this.state.selectedSortingAttribute.setSelectedSortingOrder(
            SortOrderEnum.Asc
          ),
        },
        () => {
          this.computeFilterAndReSubmitQuery(this.state.queryText);
        }
      );
    } else if (sortingOrder === SortOrderEnum.Asc) {
      this.setState(
        {
          selectedSortingAttribute: this.state.selectedSortingAttribute.setSelectedSortingOrder(
            SortOrderEnum.Desc
          ),
        },
        () => {
          this.computeFilterAndReSubmitQuery(this.state.queryText);
        }
      );
    }
  };

  openResultDialog = async (resultItem: Kendra.QueryResultItem) => {
    this.setState({
      dialogResultItem: resultItem
    });

    console.log(resultItem);

    this.setState({
      dialogResultOpen: true,
    })

    if (resultItem.DocumentId !== undefined) {
      contentLibraryApi.getDocumentMetadata(resultItem.DocumentId).then(res => {
        this.setState({
          dialogResultMetadata: res
        })
      });
    }
  }

  handleDialogClose = () => {
    this.setState({
      dialogResultOpen: false,
      dialogResultItem: undefined,
      dialogResultMetadata: undefined,
    })
  }

  handleSourceChange = async (event: any) => {

    let currentFilters = [
      ...this.state.searchFilters
    ];

    const existing = currentFilters.indexOf(event.target.name);

    if (existing >= 0) {
      currentFilters = currentFilters.filter((value) => {
        return value !== event.target.name
      });
    }
    else {
      currentFilters.push(event.target.name);
    }

    this.setState(
      {
        searchFilters: currentFilters
      }
    )

    if (this.state.queryText.length > 0) {
      this.getResults(this.state.queryText, this.state.currentPageNumber);
    }
  }

  renderSources() {
    if (this.state.availableSources.length === 0) {
      return <p>Loading...</p>
    }

    return this.state.availableSources.map((source, index) => {
      return <FormControlLabel
        key={index}
        control={
          <Checkbox name={source.Id}
            onChange={this.handleSourceChange} />
        }
        label={AvailableSourceResponse.parseFriendlyName(source.Name)}
      />
    })
  }

  private launchDocumentView = (result: Kendra.QueryResultItem) => {
    if (result.DocumentId !== undefined && result.DocumentId?.indexOf('https') < 0) {

      const splitDocumentId = result.DocumentId?.split('/');

      const searchDocumentId = splitDocumentId.slice(3).join('%2F');

      window.open('./view-document/' + searchDocumentId, '_blank');
    }
    else {
      window.open(result.DocumentURI);
    }
  }


  render() {
    return (
      <div className="searchWrapper">
        <div className="filterPane">
          <div className="stickyFilters">
            <h2 className="filter-header filter-header-title">
              Filters
            </h2>
            <FormControl sx={{ m: 3 }} component="fieldset" className="full-width" variant="standard">
              <FormLabel className="filter-header" component="legend">Source</FormLabel>
              <FormGroup className="filter-wrapper" >
                {this.renderSources()}
              </FormGroup>
            </FormControl>
          </div>
          <div className="coming-soon-filters">
            <FormLabel className="filter-header" component="legend">Sector (Coming Soon)</FormLabel>
            <FormLabel className="filter-header" component="legend">Industry (Coming Soon)</FormLabel>
            <FormLabel className="filter-header" component="legend">Service (Coming Soon)</FormLabel>
          </div>
        </div>
        <div className="results">
          {this.state.error && this.getErrorNotification()}
          <SearchBar onSubmit={this.getResults} />
          {this.state.queryText && this.state.dataReady && (
            <div className="search-container">
              {/* <div className="search-facet-container">
                <Facets
                  availableFacets={this.state.availableFacets}
                  attributeTypeLookup={this.state.attributeTypeLookup}
                  dataSourceNameLookup={this.state.dataSourceNameLookup}
                  onSelectedFacetsChanged={this.onSelectedFacetsChanged}
                  selectedFacets={this.state.selectedFacets}
                  index={this.state.index}
                  open={this.state.facetsOpen}
                  onExpand={this.handleClickExpander}
                />
              </div> */}
              <div className="searchResults">
                {this.state.searchResults.TotalNumberOfResults === 0 && (
                  <div className="empty-results center-align">
                    Kendra didn't match any results to your query.
                  </div>
                )}
                {this.state.searchResults.TotalNumberOfResults !== 0 && (
                  <div>
                    <ResultPanel
                      results={this.state.searchResults}
                      topResults={this.state.topResults}
                      faqResults={this.state.faqResults}
                      docResults={this.state.docResults}
                      dataReady={this.state.dataReady}
                      currentPageNumber={this.state.currentPageNumber}
                      submitFeedback={this.submitFeedback}
                      availableSortingAttributes={
                        this.state.availableSortingAttributes
                      }
                      selectedSortingAttribute={
                        this.state.selectedSortingAttribute
                      }
                      onSortingAttributeChange={this.onSortingAttributeChange}
                      onSortingOrderChange={this.onSortingOrderChange}
                      openResultDialog={this.openResultDialog}
                      launchDocumentView={this.launchDocumentView}
                    />
                    <Pagination
                      queryText={this.state.queryText}
                      currentPageNumber={this.state.currentPageNumber}
                      onSubmit={this.getResultsOnPageChanging}
                      results={this.state.searchResults}
                    />
                  </div>
                )}
              </div>
              <Dialog
                open={this.state.dialogResultOpen}
                onClose={this.handleDialogClose}>
                <DialogContent>
                  <DialogContentText>
                    <Typography component="p" align="left" color="textPrimary">
                      <b>Content Owner:</b> <a rel="noopener noreferrer" href='https://phonetool.amazon.com/users/crterhc' target="_blank">crterhc</a>
                    </Typography>
                    <Typography component="p" align="left" color="textPrimary">
                      <b>Content Source:</b> {this.state.dialogResultItem?.DocumentTitle?.Text}
                    </Typography>
                    <Typography component="p" align="left" color="textPrimary">
                      <b>Last Updated:</b> {DocumentMetadataResponse.generateFriendlyDate(this.state.dialogResultMetadata?.ModifiedTimestamp)}
                    </Typography>
                    <Typography component="p" align="left" color="textPrimary">
                      <b>Last Updated:</b> {DocumentMetadataResponse.generateFriendlyDate(this.state.dialogResultMetadata?.ModifiedTimestamp)}
                    </Typography>
                  </DialogContentText>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {this.state.queryText && !this.state.dataReady && (
            <div className="results-section center-align">
              <Spinner
                className="result-spinner"
                animation="border"
                variant="secondary"
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}
